import { ChevronDown, Plus } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import type {
  PackagingItem,
  PackagingSupplierPackagedItem,
} from '@/shared/hooks/api/packagingSuppliers'
import { Alert, AlertDescription } from '@/components/ui/alert'
import { Button } from '@/components/ui/button'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import {
  Empty,
  EmptyContent,
  EmptyHeader,
  EmptyTitle,
} from '@/components/ui/empty'
import type { SupplierPackagingCatalogRow } from './mapSupplierPackagingCatalogRows'
import { SupplierPartPackagingTable } from './SupplierPartPackagingTable'
import { SupplierYourPackagingItemsTable } from './SupplierYourPackagingItemsTable'

export type AddPackagingItemTarget =
  | { type: 'standalone' }
  | { type: 'forPart' }
  | { type: 'packedItem'; packagedItemId: string; packedItemSku: string }

type SupplierDetailPackagingCatalogProps = {
  packagingSupplierId: string
  packagedItems: PackagingSupplierPackagedItem[]
  packagingItems: PackagingItem[]
  allPackagingItems: SupplierPackagingCatalogRow[]
  canWriteOrgData: boolean
  hasSupplierAddress: boolean
  onAddItem: (target: AddPackagingItemTarget) => void
  onDeleteItem: (itemId: string) => void
  isDeleting: boolean
}

function AddPackagingDropdown({
  canWriteOrgData,
  hasSupplierAddress,
  onAddItem,
}: {
  canWriteOrgData: boolean
  hasSupplierAddress: boolean
  onAddItem: (target: AddPackagingItemTarget) => void
}) {
  const { t } = useTranslation('packagingSuppliers')

  if (!canWriteOrgData) {
    return null
  }

  if (!hasSupplierAddress) {
    return (
      <Button type='button' variant='outlinePrimary' size='sm' disabled>
        <Plus className='size-3.5' />
        {t('details.yourPackagingItems.add')}
      </Button>
    )
  }

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button type='button' variant='outlinePrimary' size='sm'>
          <Plus className='size-3.5' />
          {t('details.yourPackagingItems.add')}
          <ChevronDown className='size-3.5 opacity-70' />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align='end' className='w-72'>
        <DropdownMenuLabel>
          {t('details.yourPackagingItems.addMenuLabel')}
        </DropdownMenuLabel>
        <DropdownMenuSeparator />
        <DropdownMenuItem
          className='flex flex-col items-start gap-0.5'
          onSelect={() => {
            onAddItem({ type: 'standalone' })
          }}
        >
          <span>{t('details.yourPackagingItems.addStandaloneOption')}</span>
          <span className='text-xs font-normal text-muted-foreground'>
            {t('details.yourPackagingItems.addStandaloneDescription')}
          </span>
        </DropdownMenuItem>
        <DropdownMenuItem
          className='flex flex-col items-start gap-0.5'
          onSelect={() => {
            onAddItem({ type: 'forPart' })
          }}
        >
          <span>{t('details.yourPackagingItems.addForPartOption')}</span>
          <span className='text-xs font-normal text-muted-foreground'>
            {t('details.yourPackagingItems.addForPartDescription')}
          </span>
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  )
}

export function SupplierDetailPackagingCatalog({
  packagingSupplierId,
  packagedItems,
  packagingItems,
  allPackagingItems,
  canWriteOrgData,
  hasSupplierAddress,
  onAddItem,
  onDeleteItem,
  isDeleting,
}: SupplierDetailPackagingCatalogProps) {
  const { t } = useTranslation('packagingSuppliers')

  const showAddressWarning = canWriteOrgData && !hasSupplierAddress
  const hasCatalogContent =
    packagedItems.length > 0 || allPackagingItems.length > 0

  return (
    <div className='space-y-8'>
      {showAddressWarning && (
        <Alert variant='warning'>
          <AlertDescription>
            {t('items.manufacturer.addressRequiredHint')}
          </AlertDescription>
        </Alert>
      )}

      {!hasCatalogContent && (
        <Empty className='rounded-lg border border-dashed py-12'>
          <EmptyHeader>
            <EmptyTitle>{t('details.packagingCatalog.empty')}</EmptyTitle>
          </EmptyHeader>
          <EmptyContent>
            <AddPackagingDropdown
              canWriteOrgData={canWriteOrgData}
              hasSupplierAddress={hasSupplierAddress}
              onAddItem={onAddItem}
            />
          </EmptyContent>
        </Empty>
      )}

      {packagedItems.length > 0 && (
        <section className='space-y-3'>
          <div className='space-y-1'>
            <h3 className='text-lg font-semibold'>
              {t('details.partPackaging.title')}
            </h3>
            <p className='text-sm text-muted-foreground'>
              {t('details.partPackaging.description')}
            </p>
          </div>
          <SupplierPartPackagingTable
            packagingSupplierId={packagingSupplierId}
            packagedItems={packagedItems}
            packagingItems={packagingItems}
            canWriteOrgData={canWriteOrgData}
            hasSupplierAddress={hasSupplierAddress}
            onAddItem={onAddItem}
          />
        </section>
      )}

      {hasCatalogContent && (
        <section className='space-y-3'>
          <div className='flex flex-nowrap items-start justify-between gap-3'>
            <div className='min-w-0 flex-1 space-y-1'>
              <h3 className='text-lg font-semibold'>
                {t('details.yourPackagingItems.title')}
              </h3>
              <p className='text-sm text-muted-foreground'>
                {t('details.yourPackagingItems.description')}
              </p>
            </div>
            <div className='shrink-0'>
              <AddPackagingDropdown
                canWriteOrgData={canWriteOrgData}
                hasSupplierAddress={hasSupplierAddress}
                onAddItem={onAddItem}
              />
            </div>
          </div>

          <SupplierYourPackagingItemsTable
            packagingSupplierId={packagingSupplierId}
            items={allPackagingItems}
            packagedItems={packagedItems}
            emptyMessage={t('details.yourPackagingItems.empty')}
            canWriteOrgData={canWriteOrgData}
            hasSupplierAddress={hasSupplierAddress}
            onDeleteItem={onDeleteItem}
            isDeleting={isDeleting}
          />
        </section>
      )}
    </div>
  )
}
